import { reducer as formReducer } from 'redux-form';

import applicationReducer from './application-reducer';
import applicationSourceReducer from './application-source-reducer';
import { autosaveReducer } from './autosave-reducer';
import contactReducer from './contact-reducer';
import currentUserReducer from './current-user-reducer';
import displaySettingsReducer from './display-settings-reducer';
import employeeReducer from './employee-reducer';
import globalDialogReducer from './global-dialog-reducer';
import industryReducer from './industry-reducer';
import jobReducer from './job-reducer';
import jobRoleReducer from './job-role-reducer';
import messageReducer from './message-reducer';
import notificationReducer from './notification-reducer';
import orgReducer from './org-reducer';
import { surveyReducer } from './survey-reducer';
import todoReducer from './todo-reducer';
import usersReducer from './user-reducer';
import { accountReducer } from '../account/account-reducer';
import { accountGroupReducer } from '../account-group/account-group-reducer';
import { accountScoreGenerationLogReducer } from '../account-score-generation-log/account-score-generation-log-reducer';
import { adminReducer } from '../admin/admin-reducer';
import applicantAutomationReducer from '../applicant-automations/applicant-automation-reducer';
import { applicationEeoDataReducer } from '../application-eeo-data/application-eeo-data-reducer';
import { applicationSourceIssueReducer } from '../application-source-issue/application-source-issue-reducer';
import { applicationStatusSubscriptionReducer } from '../application-status-subscription/application-status-subscription-reducer';
import { commentTemplateReducer } from '../comment-template/comment-template-reducer';
import { commentsReducer } from '../comments/comment-reducer';
import { contactActivityReducer } from '../contact-activity/contact-activity-reducer';
import { contactFileReducer } from '../contact-file/contact-file-reducer';
import { craigslistAdReducer } from '../craigslist-ad/craigslist-ad-reducer';
import { craigslistFormattedJobMetadataReducer } from '../craigslist-formatted-job-metadata/craigslist-formatted-job-metadata-reducer';
import { criticalDatesReducer } from '../critical-dates/critical-dates-reducer';
import { employeeChangeLogReducer } from '../employee-change-logs/employee-change-log-reducer';
import { employeeDirectoryImportReducer } from '../employee-directory-import/employee-directory-import-reducer';
import { employeeGroupReducer } from '../employee-groups/employee-group-reducer';
import { featureGateReducer } from '../feature-gates/feature-gate-reducer';
import { featureUsageTaskReducer } from '../feature-usage-task/feature-usage-task-reducer';
import { hrisImportLogReducer } from '../hris-import-log/hris-import-log-reducer';
import { hrisIntegrationResponseReducer } from '../hris-integration-response/hris-integration-response-reducer';
import { jobBoostReducer } from '../job-boost/job-boost-reducer';
import { jobSourcingLogReducer } from '../job-sourcing-log/job-sourcing-log-reducer';
import { jobSummaryReducer } from '../job-summary/job-summary-reducer';
import { hrisIntegrationTypesReducer } from '../merge-integration/hris-integration-type-reducer';
import { mergeIntegrationsReducer } from '../merge-integration/merge-integration-reducer';
import { messageTemplateReducer } from '../message-templates/message-template-reducer';
import { messageTranslationReducer } from '../message-translation/message-translation-reducer';
import { notificationSettingsReducer } from '../notification-settings/notification-settings-reducer';
import { organizationScrapedImageReducer } from '../organization-scraped-image/organization-scraped-image-reducer';
import { referralsReducer } from '../referrals/referrals-reducer';
import { scheduledMessageFilesReducer } from '../scheduled-message-files/scheduled-message-files-reducer';
import shortenedUrlReducer from '../shortened-urls/shortened-url-reducer';
import { sourceTypeReducer } from '../source-type/source-type-reducer';
import { startDateTriggeredCommunicationReducer } from '../start-date-triggered-communication/start-date-triggered-communication-reducer';
import { twilioErrorLogReducer } from '../twilio-error-log/twilio-error-log-reducer';
import webhookEventSubscriptionReducer from '../webhook-event-subscription/webhook-event-subscription-reducer';

import { Action } from './utils/state-manager';

export default (state: Record<string, any> = {}, action: Action) => ({
  accountGroups: accountGroupReducer(state.accountGroups, action),
  account: accountReducer(state.account, action),
  accountScoreGenerationLogs: accountScoreGenerationLogReducer(
    state.accountScoreGenerationLogs,
    action,
  ),
  admin: adminReducer(state.admin, action),
  applicantAutomations: applicantAutomationReducer(state.applicantAutomations, action),
  applications: applicationReducer(state.applications, action),
  applicationEeoData: applicationEeoDataReducer(state.applicationEeoData, action),
  applicationSourceIssues: applicationSourceIssueReducer(state.applicationSourceIssues, action),
  applicationSources: applicationSourceReducer(state.applicationSources, action),
  applicationStatusSubscriptions: applicationStatusSubscriptionReducer(
    state.applicationStatusSubscriptions,
    action,
  ),
  comments: commentsReducer(state.comments, action),
  commentTemplates: commentTemplateReducer(state.commentTemplates, action),
  contacts: contactReducer(state.contacts, action),
  contactActivities: contactActivityReducer(state.contactActivities, action),
  contactFiles: contactFileReducer(state.contactFiles, action),
  craigslistAds: craigslistAdReducer(state.craigslistAds, action),
  craigslistFormattedJobMetadata: craigslistFormattedJobMetadataReducer(
    state.craigslistJobMetadata,
    action,
  ),
  criticalDates: criticalDatesReducer(state.criticalDates, action),
  displaySettings: displaySettingsReducer(state.displaySettings, action),
  drafts: autosaveReducer(state.drafts, action),
  employeeChangeLogs: employeeChangeLogReducer(state.employeeChangeLogs, action),
  employeeDirectoryImports: employeeDirectoryImportReducer(state.employeeDirectoryImports, action),
  employeeGroups: employeeGroupReducer(state.employeeGroups, action),
  employees: employeeReducer(state.employees, action),
  featureGates: featureGateReducer(state.featureGates, action),
  featureUsageTasks: featureUsageTaskReducer(state.featureUsageTasks, action),
  form: formReducer(state.form, action),
  globalDialogs: globalDialogReducer(state.globalDialogs, action),
  hrisImportLogs: hrisImportLogReducer(state.hrisImportLogs, action),
  hrisIntegrationResponses: hrisIntegrationResponseReducer(state.hrisIntegrationResponses, action),
  hrisIntegrationTypes: hrisIntegrationTypesReducer(state.hrisIntegrationTypes, action),
  industries: industryReducer(state.industries, action),
  jobRoles: jobRoleReducer(state.jobRoles, action),
  jobs: jobReducer(state.jobs, action),
  jobBoosts: jobBoostReducer(state.jobBoosts, action),
  jobSourcingLogs: jobSourcingLogReducer(state.jobSourcingLogs, action),
  jobSummaries: jobSummaryReducer(state.jobSummaries, action),
  messages: messageReducer(state.messages, action),
  messageTemplates: messageTemplateReducer(state.messageTemplates, action),
  messageTranslations: messageTranslationReducer(state.messageTranslations, action),
  mergeIntegrations: mergeIntegrationsReducer(state.mergeIntegrations, action),
  notifications: notificationReducer(state.notifications, action),
  notificationSettings: notificationSettingsReducer(state.notificationSettings, action),
  organizationScrapedImages: organizationScrapedImageReducer(
    state.organizationScrapedImages,
    action,
  ),
  orgs: orgReducer(state.orgs, action),
  referrals: referralsReducer(state.referrals, action),
  scheduledMessageFiles: scheduledMessageFilesReducer(state.scheduledMessageFiles, action),
  shortenedUrls: shortenedUrlReducer(state.shortenedUrls, action),
  sourceTypes: sourceTypeReducer(state.sourceTypes, action),
  startDateTriggeredCommunications: startDateTriggeredCommunicationReducer(
    state.startDateTriggeredCommunications,
    action,
  ),
  surveys: surveyReducer(state.surveys, action),
  todos: todoReducer(state.todos, action),
  twilioErrorLogs: twilioErrorLogReducer(state.twilioErrorLogs, action),
  user: currentUserReducer(state.user, action),
  users: usersReducer(state.users, action),
  webhookEventSubscriptions: webhookEventSubscriptionReducer(
    state.webhookEventSubscriptions,
    action,
  ),
});
